/**
 * Shared CRUD mutations for the world-entity smart wrappers (NPCs / Locations
 * / Factions). Each wrapper defined the same four `useCompanyMutation` blocks —
 * create / update / accept / delete — every one wired to the identical
 * "notify success + invalidate the entity list" success path and the shared
 * `extractErrorMessage` danger toast. This hook single-sources that wiring; a
 * wrapper supplies only the entity-specific API functions, its list query key,
 * and the four success labels.
 *
 * It intentionally returns the raw mutation objects (not pre-baked handlers):
 * the wrappers differ in whether they await (`mutateAsync`, so a dumb view can
 * close its modal on success) or fire-and-forget (`mutate`), and Factions
 * layers a wrapper-side confirm modal on top of delete. Bulk accept/delete and
 * relationship mutations stay in the wrappers — they diverge too much to fold
 * in without config bloat.
 */

import type { MessageDescriptor } from "@lingui/core"
import { useLingui } from "@lingui/react"
import { notifications } from "@mantine/notifications"
import { type QueryKey, type UseMutationResult, useQueryClient } from "@tanstack/react-query"
import { useCallback } from "react"
import { useAuth } from "../../hooks/useAuth"
import type { CompanyApi } from "../../hooks/useCompanyApi"
import { useCompanyMutation } from "../../hooks/useCompanyQuery"
import { extractErrorMessage } from "../../lib/api/errors"

type TOrgId = string | null

interface IWorldEntityMutationLabels {
  /** Success toast after a create. */
  created: MessageDescriptor
  /** Success toast after an edit. */
  saved: MessageDescriptor
  /** Success toast after promoting a pending entry. */
  accepted: MessageDescriptor
  /** Success toast after a delete. */
  deleted: MessageDescriptor
}

interface IWorldEntityMutationsConfig<TCreate, TPatch> {
  seriesId: string
  /** List cache-key builder — receives the active org id (org-id-first policy). */
  queryKey: (orgId: TOrgId) => QueryKey
  create: (company: CompanyApi, seriesId: string, body: TCreate) => Promise<unknown>
  update: (company: CompanyApi, seriesId: string, id: string, patch: TPatch) => Promise<unknown>
  accept: (company: CompanyApi, seriesId: string, id: string) => Promise<unknown>
  remove: (company: CompanyApi, seriesId: string, id: string) => Promise<unknown>
  labels: IWorldEntityMutationLabels
}

interface IWorldEntityUpdateVars<TPatch> {
  id: string
  patch: TPatch
}

interface IWorldEntityMutations<TCreate, TPatch> {
  createMutation: UseMutationResult<unknown, Error, TCreate>
  updateMutation: UseMutationResult<unknown, Error, IWorldEntityUpdateVars<TPatch>>
  acceptMutation: UseMutationResult<unknown, Error, string>
  deleteMutation: UseMutationResult<unknown, Error, string>
}

export function useWorldEntityMutations<TCreate, TPatch>(
  config: IWorldEntityMutationsConfig<TCreate, TPatch>,
): IWorldEntityMutations<TCreate, TPatch> {
  const { seriesId, queryKey, create, update, accept, remove, labels } = config
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const { i18n } = useLingui()

  const invalidate = useCallback((): void => {
    queryClient.invalidateQueries({ queryKey: queryKey(orgId) })
  }, [queryClient, queryKey, orgId])

  const notifyError = useCallback((err: Error): void => {
    notifications.show({ message: extractErrorMessage(err), color: "danger" })
  }, [])

  const createMutation = useCompanyMutation<unknown, TCreate>({
    mutationFn: (company, body) => create(company, seriesId, body),
    onSuccess: () => {
      notifications.show({ message: i18n._(labels.created), color: "success" })
      invalidate()
    },
    onError: notifyError,
  })

  const updateMutation = useCompanyMutation<unknown, IWorldEntityUpdateVars<TPatch>>({
    mutationFn: (company, { id, patch }) => update(company, seriesId, id, patch),
    onSuccess: () => {
      notifications.show({ message: i18n._(labels.saved), color: "success" })
      invalidate()
    },
    onError: notifyError,
  })

  const acceptMutation = useCompanyMutation<unknown, string>({
    mutationFn: (company, id) => accept(company, seriesId, id),
    onSuccess: () => {
      notifications.show({ message: i18n._(labels.accepted), color: "success" })
      invalidate()
    },
    onError: notifyError,
  })

  const deleteMutation = useCompanyMutation<unknown, string>({
    mutationFn: (company, id) => remove(company, seriesId, id),
    onSuccess: () => {
      notifications.show({ message: i18n._(labels.deleted), color: "success" })
      invalidate()
    },
    onError: notifyError,
  })

  return { createMutation, updateMutation, acceptMutation, deleteMutation }
}
